import { useMemo } from "react";
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input, type IInputProps } from "@components/inputs/Input/Input";
import { Select, type ISelectProps } from "@components/inputs/Select/Select";
import {
  HeatingTypeId,
  JointBillServiceTypeId,
} from "@services/jointBillServices/types";
import {
  formatDecimalNumberInputValue,
  parseDecimalNumberInputValue,
} from "@utils/units";

interface IFormHouseDetailsInputChildrenProps {
  JointBillServiceTypeIdField: React.FC<Partial<ISelectProps>>;
  AreaField: React.FC<Partial<IInputProps>>;
  HeatingTypeIdField: React.FC<Partial<ISelectProps>>;
}

type TFormHouseDetailsInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  jointBillServiceTypeIdFieldName: TName;
  areaFieldName: TName;
  heatingTypeIdFieldName: TName;
  control?: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  disabled?: boolean;
  children?: (props: IFormHouseDetailsInputChildrenProps) => JSX.Element;
};

const InputFieldComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  disabled,
  ...restProps
}: IInputProps & { name: TName; control?: Control<TFieldValues> }) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <Input
      {...restProps}
      {...field}
      disabled={disabled}
      error={error?.message}
      onBlur={(e) => {
        field.onBlur();
        restProps.onBlur?.(e);
      }}
    />
  );
};

const SelectFieldComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  disabled,
  ...restProps
}: ISelectProps & { name: TName; control?: Control<TFieldValues> }) => {
  const {
    field: { ref, ...restField },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <Select
      {...restProps}
      {...restField}
      disabled={disabled}
      buttonRef={ref}
      error={error?.message}
      onChange={(e) => {
        restField.onChange(e);
        restProps.onChange?.(e);
      }}
    />
  );
};

export const FormHouseDetailsInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  jointBillServiceTypeIdFieldName,
  areaFieldName,
  heatingTypeIdFieldName,
  control,
  setValue,
  children,
  disabled,
}: TFormHouseDetailsInputProps<TFieldValues, TName>) => {
  const { t } = useTranslation();

  const jointBillServiceTypeIdOptions = [
    JointBillServiceTypeId.APARTAMENT,
    JointBillServiceTypeId.COTTAGE,
    JointBillServiceTypeId.HOUSE,
  ].map((value) => ({
    label: t(`common.jointBillServiceTypeId.${value}`),
    value,
  }));

  const heatingTypeIdOptions = Object.values(HeatingTypeId).map((value) => ({
    label: t(`common.heatingTypeId.${value}`),
    value,
  }));

  const JointBillServiceTypeIdField: React.FC<Partial<ISelectProps>> = useMemo(
    () => (props: Partial<ISelectProps>) => (
      <SelectFieldComponent
        label={t("formHouseDetailsInput.fields.jointBillServiceTypeId.label")}
        {...props}
        name={jointBillServiceTypeIdFieldName}
        control={control}
        options={jointBillServiceTypeIdOptions}
      />
    ),
    [
      control,
      jointBillServiceTypeIdFieldName,
      jointBillServiceTypeIdOptions,
      t,
    ],
  );

  const AreaField: React.FC<Partial<IInputProps>> = useMemo(
    () => (props: Partial<IInputProps>) => (
      <InputFieldComponent
        label={t("formHouseDetailsInput.fields.area.label")}
        {...props}
        name={areaFieldName}
        control={control}
        onBlur={(e) => {
          setValue(
            areaFieldName,
            // @ts-expect-error - c
            formatDecimalNumberInputValue(
              Math.abs(parseDecimalNumberInputValue(e.target.value)),
              { isFixed: false },
            ),
          );
        }}
        inputMode="decimal"
      />
    ),
    [control, areaFieldName, t, setValue],
  );

  const HeatingTypeIdField: React.FC<Partial<ISelectProps>> = useMemo(
    () => (props: Partial<ISelectProps>) => (
      <SelectFieldComponent
        label={t("formHouseDetailsInput.fields.heatingTypeId.label")}
        {...props}
        name={heatingTypeIdFieldName}
        control={control}
        options={heatingTypeIdOptions}
      />
    ),
    [control, heatingTypeIdFieldName, heatingTypeIdOptions, t],
  );

  return children ? (
    children?.({
      JointBillServiceTypeIdField,
      AreaField,
      HeatingTypeIdField,
    })
  ) : (
    <div className="flex flex-col gap-4 sm:flex-row">
      <div className="flex basis-1/2">
        <JointBillServiceTypeIdField className="w-full" disabled={disabled} />
      </div>
      <div className="flex basis-1/2 gap-4">
        <AreaField className="w-full" disabled={disabled} />
        <HeatingTypeIdField className="w-full" disabled={disabled} />
      </div>
    </div>
  );
};
